import AudioManager from "../audio/AudioManager";

const URL = window.URL || window.webkitURL;

/**
 * @class
 * @memberOf P3
 */
export default class AssetManager {
    constructor() {
        /**
         * @member {object}
         * @member {signals.Signal} progress
         * @member {signals.Signal} complete
         * @member {signals.Signal} error
         * @member {signals.Signal} audioFile
         */
        this.signals = {
            progress: new signals.Signal(),
            complete: new signals.Signal(),
            error: new signals.Signal(),
            audioFile: new signals.Signal()
        };

        /**
         * @type {PIXI.loaders.Loader}
         * @private
         */
        this._loader = new PIXI.loaders.Loader();
        this._loader.on("progress", this._onLoaderProgress, this);
        this._loader.on("complete", this._onLoaderComplete, this);
        this._loader.on("error", this._onLoaderError, this);

        /**
         * @type {Array}
         * @private
         */
        this._manifest = [];

        /**
         * @type {Object}
         * @private
         */
        this._resources = {};

        const graphics = new PIXI.Graphics();
        graphics.beginFill(0x0, 0.5);
        graphics.drawRect(0, 0, 100, 100);
        graphics.endFill();
        graphics.lineStyle(2, 0xFF0000, 1);
        graphics.moveTo(0, 0);
        graphics.lineTo(100, 100);
        graphics.moveTo(100, 0);
        graphics.lineTo(0, 100);

        try {
            this._fallbackTexture = graphics.generateTexture();
        } catch(e) {
            this._fallbackTexture = PIXI.Texture.EMPTY;
        }

        // set the file extension for audio files
        if (Howler.codecs("ogg")) {
            P3.AssetManager.AUDIO_FILE_EXTENSION = ".ogg";
        } else if (Howler.codecs("m4a")) {
            P3.AssetManager.AUDIO_FILE_EXTENSION = ".m4a";
        } else if (Howler.codecs("mp3")) {
            P3.AssetManager.AUDIO_FILE_EXTENSION = ".mp3";
        }

        // audio files are treated as xhr blobs
        PIXI.loaders.Resource.setExtensionLoadType("ogg", PIXI.loaders.Resource.LOAD_TYPE.XHR);
        PIXI.loaders.Resource.setExtensionXhrType("ogg", PIXI.loaders.Resource.XHR_RESPONSE_TYPE.BLOB);
        PIXI.loaders.Resource.setExtensionLoadType("m4a", PIXI.loaders.Resource.LOAD_TYPE.XHR);
        PIXI.loaders.Resource.setExtensionXhrType("m4a", PIXI.loaders.Resource.XHR_RESPONSE_TYPE.BLOB);
        PIXI.loaders.Resource.setExtensionLoadType("mp3", PIXI.loaders.Resource.LOAD_TYPE.XHR);
        PIXI.loaders.Resource.setExtensionXhrType("mp3", PIXI.loaders.Resource.XHR_RESPONSE_TYPE.BLOB);
    }

    /**
     * @param {Array} files
     * @param {string} [path]
     * @returns {Array}
     */
    addFiles(files, path = "") {
        if (path && path.length > 0) {
            for (let i = 0; i < files.length; ++ i) {
                const url = files[i].url;
                files[i].url = path + url;
            }
        }
        this._manifest = this._manifest.concat(files);
        return this._manifest;
    }

    /**
     */
    load() {
        if (!this._manifest.length) return;

        this._loader.add(this._manifest);
        this._loader.load();
        return this._manifest;
    }

    /**
     * @param {string} name
     * @param {string} [extension]
     */
    unloadTexture(name, extension) {
        extension = "." + (extension || "png");

        const resource = this._resources[name];
        name = resource.url;
        name = name.replace(/\.[^/.]+$/, "");
        const texture = PIXI.utils.TextureCache[name + extension];
        texture.destroy(true);
    }

    /**
     * @param {string} name
     * @param {string} [extension]
     */
    texture(name, extension) {
        extension = "." + (extension || "png");

        let texture;
        try {
            texture = PIXI.Texture.fromFrame(name + extension);
        } catch (error) {
            if (this._resources[name]) {
                texture = this._resources[name].texture;
            } else {
                console.warn("texture not found :", name);
                texture = this._fallbackTexture;
            }
        }
        (texture.width % 4 !== 0 || texture.height % 4 !== 0) &&
            console.warn("texture dimensions not divisible by 4 :", name + extension, "(" + texture.width + "x" + texture.height + ")");
        return texture;
    }

    /**
     * @param {string} name
     * @returns {Object}
     */
    json(name) {
        const data = P3.utils.copyObject(this._resources[name].data);
        if (!data) {
            throw new Error("json not found: " + name);
        }
        return data;
    }

    /**
     * @param {string} name
     * @returns {Object}
     */
    spineData(name) {
        const resource = this._resources[name];
        if (!resource || (resource && !resource.spineData)) {
            throw new Error("spine data not found: " + name);
        }
        return resource.spineData;
    }

    /**
     * @param {PIXI.loaders.Loader} loader
     * @param {*} resource
     * @private
     */
    _onLoaderProgress(loader, resource) {
        // process audio files
        if (resource.xhr && resource.xhrType === PIXI.loaders.Resource.XHR_RESPONSE_TYPE.BLOB) {
            if (["ogg", "m4a", "mp3"].indexOf(resource.extension) !== -1) {
                const src = URL.createObjectURL(resource.data);
                this.signals.audioFile.dispatch(resource.name, src, resource.extension);
            }
        }
        this.signals.progress.dispatch(loader, resource, loader.progress);
    }

    /**
     * @param {PIXI.loaders.Loader} loader
     * @param {Array.<*>} resources
     * @private
     */
    _onLoaderComplete(loader, resources) {
        for (let i in resources) {
            if (resources.hasOwnProperty(i)) {
                this._resources[i] = resources[i];
            }
        }
        this._manifest.length = 0;
        this._loader.reset();

        this.signals.complete.dispatch(loader, resources);
    }

    /**
     * @param {string} error
     * @param {PIXI.loaders.Loader} loader
     * @param {Array<*>} resource
     * @private
     */
    _onLoaderError(error, loader, resource) {
        this.signals.error.dispatch(error, loader, resource);
    }

    /**
     * @returns {object}
     */
    get manifest() {
        return this._manifest.slice();
    }
}

/**
 * @type {string}
 */
AssetManager.AUDIO_FILE_EXTENSION = ".ogg";
